import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Alert,
} from '@mui/material';
import {
  Hotel,
  PersonAdd,
  LocalHospital,
  Warning,
  CheckCircle,
  SwapHoriz,
  ExitToApp,
  Person,
  TrendingUp,
  Close,
  Search,
  AutoAwesome,
  MonitorHeart,
  NoteAdd,
  Save as SaveIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { loadPendingPatientsdb ,loadWards_bed ,loadBedsByWarddb ,assignBedToPatient ,loadOccupancyStatsdb ,nurseDischargeBeddb ,patientsByWarddb, getNurseAssignedWardsdb, admissionCompliance, saveVitalsdb, saveNurseNotedb } from '../BaseURL';
import ComplianceAlerts from '../Compliance/ComplianceAlerts';
import Swal from 'sweetalert2';
import moment from "moment";
import AIDischargeNoteDialog from '../Common/AIDischargeNoteDialog';

// Session key for handing off "focus this patient in the InPatient workspace"
// across the sibling Home → InPatient navigation. Picked up by InPatient.js
// on mount, then cleared so refreshing doesn't re-trigger the jump.
const PENDING_FOCUS_KEY = 'totalcare:wardsPendingPatientFocus';

export default function WardDashboard({ navigate }) {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  const [pendingPatients, setPendingPatients] = useState([]);
  const [selectedWardPatients, setSelectedWardPatients] = useState([]);
  const [activeWardId, setActiveWardId] = useState(null);
  const [openWardDialog, setOpenWardDialog] = useState(false);


const loadPatientsByWard = async (wardId) => {
  try {
    const res = await axios.get(
      `${patientsByWarddb}?wardId=${wardId}`
    );

    if (res.data.success) {
      setSelectedWardPatients(res.data.patients);
      setActiveWardId(wardId);
      setOpenWardDialog(true); // ✅ OPEN DIALOG
    }
  } catch (err) {
    console.error("Failed to load ward patients", err);
    Swal.fire("Error", "Failed to load ward patients", "error");
  }
};


  useEffect(() => {
    loadPendingPatients();
     loadWards();
     loadStats()
     
  }, []);


  const loadPendingPatients = async () => {
    try {
      const res = await axios.get(loadPendingPatientsdb);

      if (res.data.success) {
        setPendingPatients(res.data.pendingPatients);
        console.log(res.data.pendingPatients)
      }
    } catch (err) {
      console.error("Failed to load pending patients");
    }
  };

  // Fan out one /admissionCompliance call per Discharge row whenever the
  // worklist refreshes (initial load + after a save inside the Fix
  // Compliance dialog). We only check Discharge-type rows that carry an
  // admissionDate — everything else is unaffected. Results live in
  // complianceByPatient so the row renderer can decide whether to show
  // the Fix Compliance button. Stale entries are dropped via a fresh
  // object so an unmount mid-flight doesn't leak.
  useEffect(() => {
    const targets = (pendingPatients || []).filter(
      p => p.action === 'Discharge' && p.id && p.admissionDate
    );
    if (targets.length === 0) {
      setComplianceByPatient({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        targets.map(async (p) => {
          try {
            const r = await axios.get(admissionCompliance, {
              params: { patientId: p.id, admissionDate: p.admissionDate },
            });
            if (!r.data?.success) return [p.id, 'unknown'];
            if (r.data.exempt) return [p.id, 'exempt'];
            const incomplete = (r.data.days || []).some(d => !d.complete);
            return [p.id, incomplete ? 'incomplete' : 'compliant'];
          } catch (_) {
            return [p.id, 'unknown'];
          }
        })
      );
      if (cancelled) return;
      const next = {};
      for (const [id, status] of results) next[id] = status;
      setComplianceByPatient(next);
    })();
    return () => { cancelled = true; };
  }, [pendingPatients]);


const loadBedsByWard = async (wardId) => {
  try {
    const res = await axios.get(`${loadBedsByWarddb}?wardId=${wardId}`);

    setBedsList(res.data.beds); 
  } catch (err) {
    console.error("Failed to load beds", err);
  }
};


const [stats, setStats] = useState(null);
const [occupancyData, setOccupancyData] = useState([]);
const [pieData, setPieData] = useState([]);


const loadStats = async () => {
  try {
    const res = await axios.get(loadOccupancyStatsdb);

    if (res.data.success) {
      setStats(res.data.stats);
      setOccupancyData(res.data.trend);
      setPieData(res.data.bedDistribution);
    }
  } catch (err) {
    console.error("Failed to load occupancy stats", err);
  }
};


  const dischargeReasons = [
    '1. Clinical Recovery',
    '2. Improved Condition',
    '3. Transfer to Another Facility',
    '4. Discharge Against Medical Advice (DAMA)',
    '5. Financial or Administrative Reasons',
    '6. Death',
    '7. No Improvement / Non-Responsive to Treatment',
    '8. Home-Based Follow-Up Plan',
    '9. Social or Personal Reasons'
  ];

  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeNote, setDischargeNote] = useState('');
  const [aiDischargeOpen, setAiDischargeOpen] = useState(false);

  // Per-row compliance status, keyed by patient id. Populated by a
  // background fan-out after pendingPatients loads — one /admissionCompliance
  // call per Discharge row in parallel. Used to hide the Fix Compliance
  // button when the patient is already compliant or exempt (no need to
  // crowd the row with an action the nurse can't act on).
  // Shape: { [patientId]: 'compliant' | 'exempt' | 'incomplete' | 'unknown' }
  const [complianceByPatient, setComplianceByPatient] = useState({});

  // Fix Compliance dialog — opened from the per-row "Fix Compliance" button on
  // a Discharge row when the patient hasn't met the per-day vitals/notes rule.
  const [fixComplianceOpen, setFixComplianceOpen]       = useState(false);
  const [fixCompliancePatient, setFixCompliancePatient] = useState(null);
  const [fixComplianceLoading, setFixComplianceLoading] = useState(false);
  const [fixComplianceReport, setFixComplianceReport]   = useState(null);
  const [fixComplianceError, setFixComplianceError]     = useState('');

  // Per-day drill-down inside the Fix Compliance dialog. The nurse never
  // leaves the worklist. Two views:
  //   'status' — list of incomplete days; each row is clickable.
  //   'day'    — focused on one selected date; shows ONLY the entries
  //              missing for that date (vitals, nursing note, or both) and
  //              saves them BACKDATED to that date (not today). This is the
  //              only way to satisfy an admission day that's already passed.
  const [fixView, setFixView]                 = useState('status');     // 'status' | 'day'
  const [fixSelectedDay, setFixSelectedDay]   = useState(null);         // the day object the nurse drilled into
  const [fixSaving, setFixSaving]             = useState(false);
  const [fixSavedBanner, setFixSavedBanner]   = useState('');           // green confirmation text after a save
  const [fixVitals, setFixVitals] = useState({
    tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '',
  });
  const [fixVitalErrors, setFixVitalErrors] = useState({});
  const [fixNote, setFixNote]               = useState('');

  

  // Visual style by action type — Admit / Detain / Discharge / Transfer each get their own chip
  const getActionColor = (action) => {
    if (action === 'Admit')    return 'primary';
    if (action === 'Detain')   return 'warning';
    if (action === 'Transfer') return 'info';
    return 'secondary'; // Discharge
  };

  const getActionIcon = (action) => {
    if (action === 'Admit')    return '🛏️';
    if (action === 'Detain')   return '⏱️';
    if (action === 'Transfer') return '🔁';
    return '🚪';
  };

  const getActionLabel = (action) => {
    if (action === 'Detain')   return 'Detain (24h)';
    if (action === 'Transfer') return 'Transfer-Emergency';
    return action;
  };

  const getInitials = (name) => {
    if (!name) return "";
    if (typeof name !== "string") return "";

    const parts = name.trim().split(" ");
    if (parts.length === 0) return "";

    let initials = parts[0][0] || "";
    if (parts.length > 1) {
      initials += parts[1][0] || "";
    }

    return initials.toUpperCase();
  };

const safe = (v) => (v ? v.toString().toLowerCase() : "");

const filteredPatients = useMemo(() => {
  const q = searchQuery.toLowerCase().trim();

  if (!q) return pendingPatients;

  return pendingPatients.filter((p) =>
    safe(p.name).includes(q) || safe(p.id).includes(q)
  );
}, [pendingPatients, searchQuery]);


const paginatedPatients = useMemo(() => {
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  return filteredPatients.slice(startIndex, endIndex);
}, [filteredPatients, page, rowsPerPage]);



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const COLORS = ['#f44336', '#4caf50', '#ff9800', '#2196f3'];



    const [wardsList, setWardsList] = useState([]);
    const [bedsList, setBedsList] = useState([]);
    const [selectedWardId, setSelectedWardId] = useState(null);
    const [selectedBed, setSelectedBed] = useState(null);


    const loadWards = async () => {
  try {
    const res = await axios.get(loadWards_bed); // your backend URL
    const allWards = res.data.wards || [];

    // Scope wards to the logged-in nurse's assignments.
    // Same rule as InPatient: admins see everything; nurses with no
    // assignments also see everything (default); nurses with assignments
    // see only those wards.
    const savedUser = localStorage.getItem('userInfo');
    const user = savedUser ? JSON.parse(savedUser) : null;
    const isAdmin = user?.role === 'System Administrator'
                 || user?.role === 'Head Of Assembly'
                 || user?.role === 'Head Of Department'
                 || user?.role === 'Doctor';
    const staffCode = user?.trans_code;

    if (!isAdmin && staffCode) {
      try {
        const wardRes = await axios.get(getNurseAssignedWardsdb, {
          params: { staffCode },
        });
        if (wardRes.data?.success && wardRes.data.results.length > 0) {
          const allowedIds = new Set(wardRes.data.results.map(w => Number(w.ward_id)));
          setWardsList(allWards.filter(w => allowedIds.has(Number(w.id))));
          return;
        }
      } catch {
        // fall through → show all wards if the lookup fails
      }
    }

    setWardsList(allWards);
  } catch (err) {
    console.error("Failed to load wards", err);
  }
};


const assignBed = async () => {
  if (!selectedBed || !selectedWardId || !selectedPatient) {
    Swal.fire("Error", "Please select a ward and bed", "error");
    return;
  }

  const wardName  = wardsList.find(w => w.id === selectedWardId)?.name || '—';
  const initials  = (selectedPatient.name || '?').split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join('').toUpperCase();
  const isFromER  = selectedPatient.state === 'Emergency';
  const finalState = isFromER ? 'Admission' : (selectedPatient.state || 'Admission');
  const stateBadge = isFromER
    ? `<div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEF3C7;color:#92400E;font-weight:700;font-size:0.85rem;">
         ⚠ Currently flagged as EMERGENCY by the doctor — assigning a regular ward bed will <u>convert</u> the patient to <strong>ADMISSION</strong>.
       </div>`
    : finalState === 'Detention'
      ? `<div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEF3C7;color:#92400E;font-weight:700;font-size:0.85rem;">
           ⏱ This is a <strong>DETENTION</strong> (24h observation, no ward charges if discharged within window).
         </div>`
      : `<div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#DCFCE7;color:#15803D;font-weight:700;font-size:0.85rem;">
           ✓ This is a regular <strong>ADMISSION</strong>.
         </div>`;

  const result = await Swal.fire({
    title: 'Confirm Bed Assignment',
    html: `
      <div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
          <div style="width:48px;height:48px;border-radius:50%;background:#1A9B7F;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.05rem;">${initials}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${selectedPatient.name || '—'}</div>
            <div style="color:#64748b;font-size:0.8rem;margin-top:2px;">ID: <strong>${selectedPatient.id}</strong> · ${selectedPatient.age || '—'} yrs · ${selectedPatient.gender || '—'}</div>
            <div style="color:#64748b;font-size:0.78rem;margin-top:2px;">Insurance: ${selectedPatient.plan || '—'}</div>
            <div style="color:#64748b;font-size:0.78rem;">Doctor: ${selectedPatient.doctor || '—'}</div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;">
          <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
            <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Ward</div>
            <div style="font-weight:700;color:#0f172a;margin-top:2px;">${wardName}</div>
          </div>
          <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
            <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Bed</div>
            <div style="font-weight:700;color:#0f172a;margin-top:2px;">${selectedBed.bed_number}${selectedBed.daily_rate ? ` <span style="color:#64748b;font-weight:500;font-size:0.8rem;">· GHS ${selectedBed.daily_rate}/day</span>` : ''}</div>
          </div>
        </div>

        ${stateBadge}

        <p style="margin-top:14px;color:#475569;font-size:0.82rem;">
          Once confirmed: bed marked <strong>occupied</strong>, patient becomes an <strong>inpatient</strong>, and ward charges begin accruing daily.
        </p>
      </div>
    `,
    icon: undefined,
    showCancelButton: true,
    confirmButtonText: 'Yes, Assign Bed',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#1A9B7F',
    cancelButtonColor: '#64748b',
    width: 560,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  try {
    const res = await axios.post(assignBedToPatient, {
      patientId: selectedPatient.id,
      bedId: selectedBed.id,
      wardId: selectedWardId,
      dat: selectedPatient.dat,          // IMPORTANT
      patientName: selectedPatient.name, // used in backend
    });

    Swal.fire("Success", "Bed assigned successfully!", "success");

    setSelectedPatient(null);
    setSelectedWardId(null);
    setSelectedBed(null);

    loadPendingPatients(); // refresh UI
  } catch (err) {
    console.error("Failed to assign bed", err);
    Swal.fire("Error", "Failed to assign bed", "error");
  }
};


const dischargePatient = async () => {
  if (!selectedPatient) {
    Swal.fire("Error", "No patient selected", "error");
    return;
  }

  if (!dischargeReason || !dischargeNote) {
    Swal.fire("Error", "Please fill in all discharge information", "error");
    return;
  }

  const dInitials = (selectedPatient.name || '?').split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join('').toUpperCase();
  const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));

  const result = await Swal.fire({
    title: 'Confirm Patient Discharge',
    html: `
      <div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
          <div style="width:48px;height:48px;border-radius:50%;background:#dc2626;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.05rem;">${dInitials}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${escapeHtml(selectedPatient.name)}</div>
            <div style="color:#64748b;font-size:0.8rem;margin-top:2px;">ID: <strong>${escapeHtml(selectedPatient.id)}</strong> · ${selectedPatient.age || '—'} yrs · ${escapeHtml(selectedPatient.gender)}</div>
          </div>
        </div>

        <div style="margin-top:10px;padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
          <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Reason for Discharge</div>
          <div style="color:#0f172a;font-weight:600;margin-top:2px;">${escapeHtml(dischargeReason)}</div>
        </div>

        <div style="margin-top:8px;padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
          <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Notes</div>
          <div style="color:#475569;font-size:0.85rem;margin-top:2px;white-space:pre-wrap;">${escapeHtml(dischargeNote)}</div>
        </div>

        <div style="margin-top:12px;padding:10px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;font-size:0.82rem;">
          <strong>This action cannot be undone from this screen.</strong> It will release the bed, mark the patient as discharged, finalise ward charges, and record the discharge notes.
        </div>
      </div>
    `,
    icon: undefined,
    showCancelButton: true,
    confirmButtonText: 'Yes, Discharge',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#64748b',
    width: 560,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  try {
    const currentUser = localStorage.getItem('username') || 'System';

    const res = await axios.post(nurseDischargeBeddb, {
      patientId: selectedPatient.id,
      dischargedBy: currentUser,
      dischargeReason: dischargeReason,
      dischargeNote: dischargeNote
    });

    if (res.data.success) {
      Swal.fire({
        icon: "success",
        title: "Patient Discharged",
        text: res.data.message,
        confirmButtonColor: "#2e7d32"
      });

      // Reset states
      setSelectedPatient(null);
      setDischargeReason('');
      setDischargeNote('');

      // Refresh data
      loadPendingPatients();
      loadWards();
      loadStats();
    } else {
      Swal.fire("Error", res.data.message, "error");
    }
  } catch (err) {
    console.error("Failed to discharge patient", err);
    const data = err.response?.data;
    if (err.response?.status === 409 && data?.blocked && Array.isArray(data.incompleteDays)) {
      const minV = data.minVitalsPerDay ?? data.minPerDay ?? 3;
      const minN = data.minNotesPerDay  ?? 1;
      const rows = data.incompleteDays.map(d => {
        const need = [];
        if ((d.missingVitals ?? 0) > 0) need.push(`${d.missingVitals} more vital${d.missingVitals === 1 ? '' : 's'}`);
        if ((d.missingNotes  ?? 0) > 0) need.push(`${d.missingNotes} more note${d.missingNotes === 1 ? '' : 's'}`);
        return `
          <div style="display:flex;justify-content:space-between;gap:8px;padding:8px 10px;border:1px solid #fde68a;background:#fffbeb;border-radius:8px;margin-bottom:6px;">
            <div style="font-weight:700;color:#92400e;">${d.day || d.date || ''}</div>
            <div style="color:#92400e;font-size:0.85rem;">
              vitals: <strong>${d.vitals ?? 0}/${minV}</strong> · notes: <strong>${d.notes ?? 0}/${minN}</strong>
              ${need.length ? `<div style="font-size:0.78rem;color:#b45309;margin-top:2px;">Need: ${need.join(', ')}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      Swal.fire({
        icon: 'warning',
        title: 'Discharge Blocked — Compliance Not Met',
        html: `<p style="color:#475569;margin:0 0 8px;font-size:13px;">
             Every admission day must have at least <strong>${minV} vitals</strong> and <strong>${minN} nursing note${minN === 1 ? '' : 's'}</strong> before this patient can be discharged.
             Record the missing entries, then try again.
           </p>
           <div style="text-align:left;max-height:240px;overflow-y:auto;">${rows}</div>`,
        confirmButtonColor: '#b45309',
        width: 560,
      });
      return;
    }
    Swal.fire("Error", data?.message || "Failed to discharge patient", "error");
  }
};

// ── Fix Compliance handlers ─────────────────────────────────────────
// Centralized compliance fetch — used on open and after every successful
// vitals / note save so the dialog reflects the new state in place.
const refreshFixCompliance = async (patient) => {
  if (!patient?.admissionDate) {
    setFixComplianceError('No admission date on file for this patient. Vitals and notes can still be saved below.');
    setFixComplianceReport(null);
    return;
  }
  setFixComplianceLoading(true);
  setFixComplianceError('');
  try {
    const res = await axios.get(admissionCompliance, {
      params: { patientId: patient.id, admissionDate: patient.admissionDate },
    });
    if (res.data?.success) {
      setFixComplianceReport(res.data);
    } else {
      setFixComplianceError(res.data?.message || 'Could not load compliance status.');
    }
  } catch (err) {
    setFixComplianceError(err.response?.data?.message || err.message || 'Could not load compliance status.');
  } finally {
    setFixComplianceLoading(false);
  }
};

const openFixCompliance = async (patient) => {
  setFixCompliancePatient(patient);
  setFixComplianceReport(null);
  setFixComplianceError('');
  setFixView('status');
  setFixSelectedDay(null);
  setFixSavedBanner('');
  setFixVitals({ tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '' });
  setFixVitalErrors({});
  setFixNote('');
  setFixComplianceOpen(true);
  await refreshFixCompliance(patient);
};

const closeFixCompliance = () => {
  if (fixSaving) return;
  setFixComplianceOpen(false);
  setFixCompliancePatient(null);
  setFixComplianceReport(null);
  setFixComplianceError('');
  setFixView('status');
  setFixSelectedDay(null);
  setFixSavedBanner('');
  setFixVitals({ tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '' });
  setFixVitalErrors({});
  setFixNote('');
};

// Drill into a specific incomplete day. Clears any prior form state so the
// nurse starts fresh, then switches to the focused 'day' view.
const openFixDay = (day) => {
  setFixSelectedDay(day);
  setFixView('day');
  setFixSavedBanner('');
  setFixVitals({ tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '' });
  setFixVitalErrors({});
  setFixNote('');
};

// Back to the status list. Used after a day is complete OR if the nurse
// wants to pick a different day.
const backToStatus = () => {
  setFixView('status');
  setFixSelectedDay(null);
  setFixVitals({ tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '' });
  setFixVitalErrors({});
  setFixNote('');
};

// Resolve the date string the next save should stamp. In 'day' view we use
// the selected day's date (backfill). On the status view (legacy callers)
// we'd fall back to today, but the UI no longer offers a save there.
const targetDateForSave = () => {
  if (fixView === 'day' && fixSelectedDay?.date) return fixSelectedDay.date;
  return moment().format('YYYY-MM-DD');
};

// BMI helper — mirrors the existing InPatient.js calculator so the same
// derived value lands in the vitals row regardless of which workspace the
// nurse used to record it.
const fixCalcBMI = (we, high) => {
  const w = parseFloat(we); const h = parseFloat(high);
  if (!w || !h || w <= 0 || h <= 0) return '';
  const m = h / 100;
  return (w / (m * m)).toFixed(2);
};

// Mirrors the validate logic from InPatient.handleSaveVitals: temperature is
// required; everything else is optional but range-checked when present.
const validateFixVitals = () => {
  const errs = {};
  const t = parseFloat(fixVitals.tem);
  if (!fixVitals.tem || isNaN(t) || t < 30 || t > 45) errs.tem = true;
  if (fixVitals.pulse && (parseFloat(fixVitals.pulse) < 40 || parseFloat(fixVitals.pulse) > 200)) errs.pulse = true;
  if (fixVitals.res   && (parseFloat(fixVitals.res)   < 8  || parseFloat(fixVitals.res)   > 40))  errs.res = true;
  if (fixVitals.spo2  && (parseFloat(fixVitals.spo2)  < 70 || parseFloat(fixVitals.spo2)  > 100)) errs.spo2 = true;
  if (fixVitals.pre) {
    const m = /^(\d{2,3})\/(\d{2,3})$/.exec(fixVitals.pre.trim());
    if (!m) errs.pre = true;
    else {
      const s = +m[1]; const d = +m[2];
      if (s < 70 || s > 250 || d < 40 || d > 150) errs.pre = true;
    }
  }
  setFixVitalErrors(errs);
  return Object.keys(errs).length === 0;
};

// Resolve the current logged-in user once per save — same shape as
// InPatient.handleSaveVitals / handleSaveNurseNote so the audit fields
// (test_by, user) stay consistent across both entry surfaces.
const getCurrentNurseName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return u?.name || '';
  } catch (_) { return ''; }
};

// Refresh the report AND re-resolve the selected day so the day view's
// remaining-needs counters update in place after every save. If the day is
// now complete, snap back to the status list with a success banner.
const refreshThenReconcileDay = async () => {
  const fresh = await refreshFixComplianceReturning(fixCompliancePatient);
  loadPendingPatients();
  if (fixView === 'day' && fixSelectedDay) {
    const updated = (fresh?.days || []).find(d => d.date === fixSelectedDay.date);
    if (updated && updated.complete) {
      backToStatus();
      setFixSavedBanner(`Day ${fixSelectedDay.date} is now complete.`);
    } else if (updated) {
      setFixSelectedDay(updated);
    }
  }
};

// Wraps refreshFixCompliance so the caller can react to the new report
// without waiting on a re-render. Used by refreshThenReconcileDay above.
const refreshFixComplianceReturning = async (patient) => {
  if (!patient?.admissionDate) return null;
  try {
    const res = await axios.get(admissionCompliance, {
      params: { patientId: patient.id, admissionDate: patient.admissionDate },
    });
    if (res.data?.success) {
      setFixComplianceReport(res.data);
      return res.data;
    }
  } catch (_) { /* swallow — banner will already have shown the save outcome */ }
  return null;
};

const handleSaveFixVitals = async () => {
  if (!fixCompliancePatient) return;
  if (!validateFixVitals()) return;
  setFixSaving(true);
  setFixSavedBanner('');
  try {
    const bmi = fixCalcBMI(fixVitals.we, fixVitals.high);
    const targetDate = targetDateForSave();
    const res = await axios.post(saveVitalsdb, {
      patientId: fixCompliancePatient.id,
      trans_code: null,
      tem: fixVitals.tem,
      pulse: fixVitals.pulse || null,
      res:   fixVitals.res   || null,
      pre:   fixVitals.pre   || null,
      spo2:  fixVitals.spo2  || null,
      we:    fixVitals.we    || null,
      high:  fixVitals.high  || null,
      bmi:   bmi || null,
      user: getCurrentNurseName(),
      name: fixCompliancePatient.name || '',
      age:  fixCompliancePatient.age  || '',
      gender: fixCompliancePatient.gender || '',
      status: '', // ward worklist does not carry insurance — left blank so backend uses defaults
      dat_vist: targetDate,                          // ← backdated to the selected day, not today
      time:     moment().format('HH:mm:ss'),
    });
    if (res.data?.success) {
      setFixVitals({ tem: '', pulse: '', res: '', pre: '', spo2: '', we: '', high: '' });
      setFixVitalErrors({});
      setFixSavedBanner(`Vitals saved for ${targetDate}.`);
      await refreshThenReconcileDay();
    } else {
      Swal.fire('Save failed', res.data?.message || 'Could not save vitals.', 'error');
    }
  } catch (err) {
    Swal.fire('Save failed', err.response?.data?.message || err.message || 'Could not save vitals.', 'error');
  } finally {
    setFixSaving(false);
  }
};

const handleSaveFixNote = async () => {
  if (!fixCompliancePatient) return;
  if (!fixNote.trim()) {
    Swal.fire({ icon: 'warning', title: 'Empty note', text: 'Type the nursing note before saving.', toast: true, position: 'top-end', timer: 2500, showConfirmButton: false });
    return;
  }
  setFixSaving(true);
  setFixSavedBanner('');
  try {
    const targetDate = targetDateForSave();
    const isBackfill = targetDate !== moment().format('YYYY-MM-DD');
    const res = await axios.post(saveNurseNotedb, {
      patientId: fixCompliancePatient.id,
      note: fixNote.trim(),
      user: getCurrentNurseName(),
      name: fixCompliancePatient.name || '',
      age:  fixCompliancePatient.age  || '',
      gen:  fixCompliancePatient.gender || '',
      dat:  targetDate,                              // ← backdated to the selected day
      time: moment().format('HH:mm:ss'),
      // mode:'append' bypasses the (id,user,dat) upsert on the backend so
      // backfilled notes don't overwrite anything the same nurse already
      // wrote for that date. The compliance check only cares about COUNT,
      // so adding rather than updating is the right behavior here.
      mode: isBackfill ? 'append' : undefined,
    });
    if (res.data?.success) {
      setFixNote('');
      setFixSavedBanner(`Nursing note saved for ${targetDate}.`);
      await refreshThenReconcileDay();
    } else {
      Swal.fire('Save failed', res.data?.message || 'Could not save nursing note.', 'error');
    }
  } catch (err) {
    Swal.fire('Save failed', err.response?.data?.message || err.message || 'Could not save nursing note.', 'error');
  } finally {
    setFixSaving(false);
  }
};

const fixComplianceIncomplete = (fixComplianceReport?.days || []).filter(d => !d.complete);
const minVitalsForDay = fixComplianceReport?.minVitalsPerDay ?? fixComplianceReport?.minPerDay ?? 3;
const minNotesForDay  = fixComplianceReport?.minNotesPerDay  ?? 1;

if (!stats) return null;
  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Ward Management Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time overview of all ward operations
          </Typography>
        </Box>

        {/* Compliance Alerts — self-mounts a floating action button at the
            lower-right of the viewport. Clicking a "View vitals" / "View
            notes" entry hands the patient off to the InPatient workspace via
            session storage and switches the sidebar to that section. */}
        <ComplianceAlerts
          scope="nursing"
          onSelectPatient={(row, tab) => {
            try {
              sessionStorage.setItem(
                PENDING_FOCUS_KEY,
                JSON.stringify({
                  patientId: row.id,
                  tab,                           // 'vitals' | 'notes'
                  admissionDate: row.admissionDate || null,
                  ts: Date.now(),
                })
              );
            } catch (_) {}
            if (typeof navigate === 'function') navigate('InPatient');
          }}
        />

        {/* Quick Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Total Beds
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.totalBeds}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <Hotel sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Occupied
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.occupied}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <Person sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Available
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.available}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <CheckCircle sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Occupancy Rate
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.occupancyRate}%
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <TrendingUp sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* Left Section - Pending Patients & Charts */}
          <Grid item xs={12} lg={8} size={9}>
            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
           
              <Grid item xs={12} md={8} size={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    7-Day Occupancy Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={occupancyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="rate" stroke="#667eea" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4} size={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Bed Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Pending Patients Table */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2.5,
                border: '1px solid #e5e9ef',
                bgcolor: '#fff',
                overflow: 'hidden',
                fontFamily: '"Inter","Segoe UI",Arial,sans-serif',
              }}
            >
              {/* Header */}
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e5e9ef' }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography sx={{
                      fontSize: '0.7rem', fontWeight: 700,
                      color: '#1A9B7F', letterSpacing: '1.4px',
                      textTransform: 'uppercase', mb: 0.25,
                    }}>
                      Pending Worklist
                    </Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                      Patients Awaiting Bed Assignment &amp; Discharge
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>
                      Showing <strong>{filteredPatients.length}</strong> of {pendingPatients.length} patient{pendingPatients.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label="🛏️ Admit"        sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }} />
                    <Chip size="small" label="⏱️ Detain (24h)" sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(217,119,6,0.10)', color: '#92400e' }} />
                    <Chip size="small" label="🚪 Discharge"    sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(67,56,202,0.10)', color: '#3730a3' }} />
                  </Stack>
                </Stack>
              </Box>

              {/* Search Bar */}
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e5e9ef', bgcolor: '#fafbfc' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by patient name or ID…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    maxWidth: 420,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.75,
                      bgcolor: '#fff',
                      fontSize: '0.88rem',
                      '& fieldset': { borderColor: '#e5e9ef' },
                      '&:hover fieldset': { borderColor: '#1A9B7F' },
                      '&.Mui-focused fieldset': { borderColor: '#1A9B7F', borderWidth: 1.5 },
                      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(26,155,127,0.10)' },
                    },
                  }}
                />
              </Box>

              {/* Table */}
              <TableContainer>
                <Table size="small" sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: '1px solid #e5e9ef' } }}>
                      {[
                        { label: 'Patient',     w: '24%' },
                        { label: 'Age / Gender',w: '11%' },
                        { label: 'Plan',        w: '20%' },
                        { label: 'Type',        w: '12%' },
                        { label: 'Wait',        w: '10%' },
                        { label: 'Doctor',      w: '12%' },
                        { label: '',            w: '11%', align: 'right' },
                      ].map((c) => (
                        <TableCell
                          key={c.label || 'actions'}
                          align={c.align || 'left'}
                          sx={{
                            width: c.w,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.25,
                          }}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPatients.length > 0 ? (
                      paginatedPatients.map((patient) => {
                        const isDetain    = patient.action === 'Detain';
                        const isAdmit     = patient.action === 'Admit';
                        const isDischarge = patient.action === 'Discharge';
                        const isTransfer  = patient.action === 'Transfer';
                        const accent =
                          isDetain    ? { bg: 'rgba(217,119,6,0.10)',  text: '#92400e' } :
                          isDischarge ? { bg: 'rgba(67,56,202,0.10)',  text: '#3730a3' } :
                          isTransfer  ? { bg: 'rgba(37,99,235,0.10)',  text: '#1d4ed8' } :
                                        { bg: 'rgba(26,155,127,0.10)', text: '#0f4f3f' };

                        return (
                          <TableRow
                            key={patient.trans_code || patient.id}
                            hover
                            sx={{
                              '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                              '&:last-child td': { borderBottom: 'none' },
                            }}
                          >
                            {/* Patient */}
                            <TableCell>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Avatar sx={{
                                  width: 36, height: 36,
                                  bgcolor: accent.bg,
                                  color: accent.text,
                                  fontSize: 13, fontWeight: 700,
                                }}>
                                  {getInitials(patient.name)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {patient.name || '—'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    ID: #{String(patient.id || '').padStart(4, '0')}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            {/* Age / Gender */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                {patient.age ?? '—'}<Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, ml: 0.5 }}>yrs</Box>
                              </Typography>
                              <Chip
                                label={patient.gender || '—'}
                                size="small"
                                sx={{
                                  mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                                  bgcolor: patient.gender === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                                  color:   patient.gender === 'MALE' ? '#1D4ED8' : '#BE185D',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </TableCell>

                            {/* Plan */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: '0.82rem', color: '#334155',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.4,
                                }}
                                title={patient.plan || ''}
                              >
                                {patient.plan || '—'}
                              </Typography>
                            </TableCell>

                            {/* Type */}
                            <TableCell>
                              <Chip
                                label={`${getActionIcon(patient.action)} ${getActionLabel(patient.action) || '—'}`}
                                size="small"
                                sx={{
                                  height: 22, fontSize: '0.7rem', fontWeight: 700,
                                  bgcolor: accent.bg, color: accent.text,
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            </TableCell>

                            {/* Wait */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500 }}>
                                ⏱️ {patient.waitTime || '—'}
                              </Typography>
                            </TableCell>

                            {/* Doctor */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {patient.doctor || '—'}
                              </Typography>
                            </TableCell>

                            {/* Action button */}
                            <TableCell align="right">
                              {/*  ── A TRANSFER NEEDS A BED, NOT A DISCHARGE ─────────
                                   isTransfer is computed above and was not used here,
                                   so an ER → ward transfer fell into the else branch
                                   and the row offered "Discharge". A patient who has
                                   just been moved into the ward has not been treated
                                   yet; offering to discharge them is the opposite of
                                   the work, and it left no way to assign the bed at
                                   all — the transfer arrived and then dead-ended.

                                   Every other part of this flow already handled it:
                                   the dialog's header, its bed picker and its confirm
                                   button all test for 'Transfer', and
                                   /assignBedToPatient flips a Transfer-Emergency row
                                   to state='Admission' with status=2 (server.js:15815).
                                   This one branch was the only thing missing.  */}
                              {(isAdmit || isDetain || isTransfer) ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<Hotel sx={{ fontSize: 15 }} />}
                                  onClick={() => setSelectedPatient(patient)}
                                  sx={{
                                    textTransform: 'none', fontWeight: 600,
                                    boxShadow: 'none',
                                    //  Matches the chip on the row and the dialog this
                                    //  opens: amber for a detention, blue for an ER
                                    //  transfer, teal for a plain admission. A blue
                                    //  chip that opens a blue dialog behind a teal
                                    //  button is the screen disagreeing with itself.
                                    bgcolor: isDetain ? '#d97706' : isTransfer ? '#2563eb' : '#1A9B7F',
                                    '&:hover': {
                                      bgcolor: isDetain ? '#b45309' : isTransfer ? '#1d4ed8' : '#157763',
                                      boxShadow: isDetain
                                        ? '0 4px 12px rgba(217,119,6,0.25)'
                                        : isTransfer
                                          ? '0 4px 12px rgba(37,99,235,0.25)'
                                          : '0 4px 12px rgba(26,155,127,0.25)',
                                    },
                                  }}
                                >
                                  {isTransfer ? 'Assign Bed — Transfer' : 'Assign Bed'}
                                </Button>
                              ) : (
                                // Only surface the Fix Compliance button for rows whose
                                // compliance status is actually 'incomplete' (or 'unknown',
                                // i.e. the per-row check failed — better to show it than hide
                                // a needed action). Compliant + exempt rows go straight to
                                // Discharge with no extra button cluttering the row.
                                (() => {
                                  const status = complianceByPatient[patient.id];
                                  const showFix = status === 'incomplete' || status === 'unknown';
                                  return (
                                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                                      {showFix && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<CheckCircle sx={{ fontSize: 15 }} />}
                                          onClick={() => openFixCompliance(patient)}
                                          sx={{
                                            textTransform: 'none', fontWeight: 600,
                                            borderColor: '#d97706', color: '#92400e',
                                            '&:hover': { borderColor: '#b45309', bgcolor: 'rgba(217,119,6,0.08)' },
                                          }}
                                        >
                                          Fix Compliance
                                        </Button>
                                      )}
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ExitToApp sx={{ fontSize: 15 }} />}
                                        onClick={() => setSelectedPatient(patient)}
                                        sx={{
                                          textTransform: 'none', fontWeight: 600,
                                          borderColor: '#4338ca', color: '#4338ca',
                                          '&:hover': { borderColor: '#3730a3', bgcolor: 'rgba(67,56,202,0.06)' },
                                        }}
                                      >
                                        Discharge
                                      </Button>
                                    </Stack>
                                  );
                                })()
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                          <PersonAdd sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                            {searchQuery
                              ? `No patients match "${searchQuery}"`
                              : 'No patients pending bed assignment or discharge.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredPatients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid #e5e9ef',
                  '& .MuiTablePagination-toolbar': { minHeight: 48 },
                }}
              />
            </Paper>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} lg={4} size={3}>
            {/* Today's Summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Today's Summary
              </Typography>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                    <PersonAdd />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Admissions Today
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.todayAdmissions}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'error.light', width: 48, height: 48 }}>
                    <ExitToApp />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Expected Discharges
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.expectedDischarges}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Ward Overview */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Ward Overview
              </Typography>
              <Stack spacing={3}>
               {wardsList.map((ward) => (
                <Box
                  key={ward.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => loadPatientsByWard(ward.id)}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={activeWardId === ward.id ? 700 : 500}
                      color={activeWardId === ward.id ? "primary.main" : "text.primary"}
                    >
                      {ward.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ward.occupied}/{ward.capacity}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={(ward.occupied / ward.capacity) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}


              </Stack>
            </Paper>

          

          </Grid>
        </Grid>

       
        <Dialog 
          open={Boolean(selectedPatient)} 
          onClose={() => {
            setSelectedPatient(null);
            setSelectedWardId(null);
            setSelectedBed(null);
            setDischargeReason('');
            setDischargeNote('');
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          {/* Header with colored background — different gradient per action type */}
          <DialogTitle
            sx={{
              background:
                selectedPatient?.action === 'Detain'
                  ? 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)'        // system teal for detention
                  : selectedPatient?.action === 'Admit'
                  ? 'linear-gradient(135deg, #1c7a24ff 0%, #23913eff 100%)'    // green for admission
                  : selectedPatient?.action === 'Transfer'
                  ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'        // blue for ER → Ward transfer
                  : 'linear-gradient(135deg, #3ba53bff 0%, #23913eff 100%)',   // discharge
              color: 'white',
              pb: 3
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {selectedPatient?.action === 'Detain'
                    ? '⏱️ Assign Bed — Detention (24h)'
                    : selectedPatient?.action === 'Admit'
                    ? '🛏️ Assign Bed to Patient'
                    : selectedPatient?.action === 'Transfer'
                    ? '🔁 Assign Bed — Transfer from Emergency'
                    : '🚪 Process Patient Discharge'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  {selectedPatient?.action === 'Detain'
                    ? `Select an available bed for the detained patient ${selectedPatient?.name}. The 24-hour observation timer starts after bed assignment.`
                    : selectedPatient?.action === 'Admit'
                    ? `Select an available bed for ${selectedPatient?.name}`
                    : selectedPatient?.action === 'Transfer'
                    ? `Select a new ward bed for ${selectedPatient?.name}. The patient's days-at-facility counter continues from the original ER admission — only the bed changes.`
                    : `Complete discharge process for ${selectedPatient?.name}`}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => {
                  setSelectedPatient(null);
                  setSelectedWardId(null);
                  setSelectedBed(null);
                  setDischargeReason('');
                  setDischargeNote('');
                }}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 4, pb: 3 }}>
            {selectedPatient && (
              <>
                {/* Patient Information Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    mt: 2,
                    border: '2px solid',
                    borderColor:
                      selectedPatient.action === 'Detain' ? 'primary.main'
                      : selectedPatient.action === 'Admit' ? 'primary.main'
                      : 'secondary.main',
                    borderRadius: 2,
                    bgcolor:
                      selectedPatient.action === 'Detain' ? 'primary.50'
                      : selectedPatient.action === 'Admit' ? 'primary.50'
                      : 'secondary.50'
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    📋 Patient Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Patient Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Age / Gender
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.age} years / {selectedPatient.gender}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Plan
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.plan}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Action Type
                        </Typography>
                        <Chip
                          label={`${getActionIcon(selectedPatient.action)} ${getActionLabel(selectedPatient.action)}`}
                          size="medium"
                          sx={{
                            fontWeight: 600,
                            // Detain uses the system teal in this dialog (not amber)
                            // so it matches the rest of the dialog chrome
                            ...(selectedPatient.action === 'Detain'
                              ? { bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }
                              : selectedPatient.action === 'Admit'
                              ? { bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }
                              : { bgcolor: 'rgba(67,56,202,0.10)',  color: '#3730a3' }),
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Wait Time
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ⏱️ {selectedPatient.waitTime}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Doctor
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.doctor || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Conditional Content — bed-assignment (Admit OR Detain) vs Discharge */}
                {(selectedPatient.action === 'Admit' || selectedPatient.action === 'Detain' || selectedPatient.action === 'Transfer') ? (
                  <Box>
                    {/* Ward Selection */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        🏥 Step 1: Select Ward
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Ward *</InputLabel>
                        <Select
                          value={selectedWardId || ""}
                          label="Select Ward *"
                          onChange={(e) => {
                            setSelectedWardId(e.target.value);
                            loadBedsByWard(e.target.value);
                            setSelectedBed(null);
                          }}
                          sx={{ bgcolor: 'white' }}
                        >
                          {wardsList.map((ward) => (
                            <MenuItem key={ward.id} value={ward.id}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <LocalHospital fontSize="small" color="primary" />
                                <Typography>{ward.name}</Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Bed Selection */}
                    {selectedWardId && (
                      <Box>
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                          🛏️ Step 2: Select Available Bed
                        </Typography>
                        
                        {bedsList.length === 0 ? (
                          <Paper 
                            sx={{ 
                              p: 4, 
                              textAlign: 'center',
                              bgcolor: 'grey.50',
                              border: '2px dashed',
                              borderColor: 'grey.300'
                            }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              No available beds in this ward
                            </Typography>
                          </Paper>
                        ) : (
                          <Grid container spacing={2}>
                            {bedsList.map((bed) => (
                              <Grid item xs={6} sm={4} md={3} key={bed.id}>
                                <Paper
                                  onClick={() => setSelectedBed(bed)}
                                  elevation={selectedBed?.id === bed.id ? 8 : 2}
                                  sx={{
                                    p: 3,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    bgcolor: selectedBed?.id === bed.id ? "success.main" : "white",
                                    color: selectedBed?.id === bed.id ? "white" : "text.primary",
                                    border: '2px solid',
                                    borderColor: selectedBed?.id === bed.id ? "success.dark" : "grey.200",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateY(-8px)",
                                      boxShadow: 6,
                                      borderColor: "success.main",
                                      bgcolor: selectedBed?.id === bed.id ? "success.dark" : "success.50"
                                    },
                                  }}
                                >
                                  <Hotel sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography variant="h6" fontWeight={700}>
                                    {bed.bed_number}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {selectedBed?.id === bed.id ? '✓ Selected' : 'Available'}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                      📝 Discharge Information
                    </Typography>
                    <Stack spacing={3}>
                      <FormControl fullWidth>
                        <InputLabel>Reason for Discharge *</InputLabel>
                        <Select
                          value={dischargeReason}
                          label="Reason for Discharge *"
                          onChange={(e) => setDischargeReason(e.target.value)}
                          sx={{ bgcolor: 'white' }}
                        >
                          {dischargeReasons.map((reason, index) => (
                            <MenuItem key={index} value={reason}>
                              {reason}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoAwesome />}
                            onClick={() => setAiDischargeOpen(true)}
                            disabled={!selectedPatient?.id}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.74rem',
                              borderColor: '#6a11cb', color: '#6a11cb',
                              '&:hover': { borderColor: '#6a11cb', bgcolor: 'rgba(106,17,203,0.06)' },
                            }}
                          >
                            Generate with AI
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          label="Discharge Notes *"
                          multiline
                          rows={8}
                          value={dischargeNote}
                          onChange={(e) => setDischargeNote(e.target.value)}
                          placeholder="Enter detailed discharge notes including final diagnosis, treatment summary, medications, follow-up, and special care instructions."
                          helperText="Provide comprehensive discharge information for continuity of care"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 3, 
                          bgcolor: 'info.50',
                          border: '1px solid',
                          borderColor: 'info.200',
                          borderRadius: 2
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle2" fontWeight={700} color="info.dark">
                            ℹ️ Important Discharge Checklist
                          </Typography>
                          {[
                            'Ensure all medications are prescribed and explained to patient/family',
                            'Schedule follow-up appointment before discharge',
                            'Provide written discharge instructions',
                            'Complete all pending billing procedures',
                            'Verify emergency contact information is current'
                          ].map((item, idx) => (
                            <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                              <CheckCircle sx={{ fontSize: 18, color: 'info.main', mt: 0.2 }} />
                              <Typography variant="body2" color="info.dark">
                                {item}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </DialogContent>

         <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={() => {
                setSelectedPatient(null);
                setSelectedWardId(null);
                setSelectedBed(null);
                setDischargeReason('');
                setDischargeNote('');
              }}
              size="large"
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>

            {(selectedPatient?.action === "Admit" || selectedPatient?.action === "Detain" || selectedPatient?.action === "Transfer") ? (
              <Button
                variant="contained"
                size="large"
                disabled={!selectedBed}
                onClick={() => assignBed()}
                startIcon={<Hotel />}
                sx={{ minWidth: 200 }}
              >
                {selectedBed
                  ? (selectedPatient?.action === "Detain" ? 'Confirm Detention Bed'
                     : selectedPatient?.action === "Transfer" ? 'Confirm Ward Transfer'
                     : 'Confirm Bed Assignment')
                  : 'Select a Bed First'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                disabled={!dischargeReason || !dischargeNote}
                onClick={() => dischargePatient()} // ✅ ADD THIS
                startIcon={<ExitToApp />}
                sx={{ minWidth: 200 }}
              >
                Complete Discharge
              </Button>
            )}
          </DialogActions>
        </Dialog>
        

      <Dialog
        open={openWardDialog}
        onClose={() => setOpenWardDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #389b38ff 0%, #3f922bff 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Hotel sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Ward Patient List
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {selectedWardPatients.length} patient{selectedWardPatients.length !== 1 ? 's' : ''} currently admitted
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {selectedWardPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Hotel sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Patients in Ward
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This ward currently has no admitted patients
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Patient Information
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Age / Gender
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Bed Number
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Admission Date
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                      align="center"
                    >
                      Days Spent
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedWardPatients.map((p, index) => {
                    // Calculate days spent from admission date to now
                    const admissionDate = moment(p.admission_date || p.admitted_date);
                    const currentDate = moment();
                    const daysSpent = currentDate.diff(admissionDate, 'days');
                    
                    // Determine color based on length of stay
                    let daysColor = 'success.main';
                    if (daysSpent > 14) {
                      daysColor = 'error.main';
                    } else if (daysSpent > 7) {
                      daysColor = 'warning.main';
                    }

                    return (
                      <TableRow 
                        key={p.patient_id}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                          '&:hover': { 
                            bgcolor: 'primary.lighter',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={index + 1}
                            size="small"
                            color="primary"
                            sx={{ 
                              fontWeight: 700,
                              minWidth: 32
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar 
                              sx={{ 
                                bgcolor: p.gender === 'MALE' ? 'info.main' : 'error.main',
                                width: 40,
                                height: 40,
                                fontWeight: 700
                              }}
                            >
                              {p.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {p.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {p.patientId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" gap={0.5}>
                            <Chip
                              label={`${p.age} yrs`}
                              size="small"
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                            <Chip
                              label={p.gender}
                              size="small"
                              color={p.gender === 'MALE' ? 'info' : 'error'}
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24,
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: 'success.lighter',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'success.main'
                            }}
                          >
                            <Hotel sx={{ fontSize: 18, color: 'success.dark' }} />
                            <Typography 
                              variant="body2" 
                              fontWeight={700}
                              color="success.dark"
                            >
                              {p.bed_number}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {moment(p.admission_date || p.admitted_date).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(p.admission_date || p.admitted_date).format('hh:mm A')}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: `${daysColor}15`,
                                border: '2px solid',
                                borderColor: daysColor,
                                borderRadius: '50%',
                                width: 56,
                                height: 56,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column'
                              }}
                            >
                              <Typography 
                                variant="h6" 
                                fontWeight={700}
                                sx={{ color: daysColor, lineHeight: 1 }}
                              >
                                {daysSpent}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: daysColor,
                                  fontSize: '0.65rem',
                                  fontWeight: 600
                                }}
                              >
                                {daysSpent === 1 ? 'day' : 'days'}
                              </Typography>
                            </Box>
                            
                            {daysSpent > 14 && (
                              <Chip
                                label="Long Stay"
                                size="small"
                                color="error"
                                sx={{ 
                                  fontSize: '0.65rem',
                                  height: 20,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Summary Footer */}
          {selectedWardPatients.length > 0 && (
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50',
                borderTop: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'primary.lighter',
                      border: '1px solid',
                      borderColor: 'primary.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="primary.dark">
                      {selectedWardPatients.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Total Patients
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'success.lighter',
                      border: '1px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="success.dark">
                      {selectedWardPatients.filter(p => {
                        const admissionDate = moment(p.admission_date || p.admitted_date);
                        const currentDate = moment();
                        const daysSpent = currentDate.diff(admissionDate, 'days');
                        return daysSpent <= 7;
                      }).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Recent Admissions (≤7 days)
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'error.lighter',
                      border: '1px solid',
                      borderColor: 'error.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="error.dark">
                      {selectedWardPatients.filter(p => {
                        const admissionDate = moment(p.admission_date || p.admitted_date);
                        const currentDate = moment();
                        const daysSpent = currentDate.diff(admissionDate, 'days');
                        return daysSpent > 14;
                      }).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Long Stay (&gt;14 days)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button 
            onClick={() => setOpenWardDialog(false)}
            variant="contained"
            color="primary"
            startIcon={<Close />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AIDischargeNoteDialog
        open={aiDischargeOpen}
        onClose={() => setAiDischargeOpen(false)}
        patient={selectedPatient}
        dischargeReason={dischargeReason}
        onGenerated={(text) => setDischargeNote(text)}
      />

      {/* ── Fix Compliance Dialog ─────────────────────────────────────────
          Shown when the nurse clicks "Fix Compliance" next to Discharge on a
          patient who is blocked by the per-day vitals/notes rule. Lists each
          incomplete day and offers two shortcut actions that hand the nurse
          off to the InPatient workspace with the vitals or nursing-note tab
          pre-focused — so they can finish recording, return here, and click
          Discharge. */}
      <Dialog
        open={fixComplianceOpen}
        onClose={closeFixCompliance}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
          bgcolor: '#d97706', color: 'white',
        }}>
          <Warning />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              Complete Compliance to Discharge
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.78rem', opacity: 0.95 }}>
              {fixCompliancePatient?.name || ''}
            </Typography>
          </Box>
          <IconButton onClick={closeFixCompliance} sx={{ color: 'white' }} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Two views inside one dialog:
              · 'status' — list of incomplete days, each clickable.
              · 'day'    — focused on one selected date; only shows entry
                           forms for what's actually missing on that date.
            Saves are backdated to the selected day so they actually move
            the compliance needle for THAT day. */}
        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {fixSavedBanner && (
            <Alert severity="success" variant="outlined" sx={{ mb: 1.5, fontSize: '0.82rem' }}>
              {fixSavedBanner}
            </Alert>
          )}

          {/* ── STATUS VIEW ──────────────────────────────────────────── */}
          {fixView === 'status' && (
            fixComplianceLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3, justifyContent: 'center' }}>
                <LinearProgress sx={{ flex: 1, height: 6, borderRadius: 3 }} />
              </Stack>
            ) : fixComplianceError ? (
              <Paper elevation={0} sx={{
                p: 1.75, borderRadius: 2,
                border: '1px solid #fecaca', bgcolor: '#FEF2F2',
              }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#991B1B', fontWeight: 700 }}>
                  {fixComplianceError}
                </Typography>
              </Paper>
            ) : fixComplianceReport?.exempt ? (
              <Paper elevation={0} sx={{
                p: 1.75, borderRadius: 2,
                border: '1px solid #BBF7D0', bgcolor: '#ECFDF5',
              }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#065F46', fontWeight: 700 }}>
                  This admission is exempt from the per-day rule. You can discharge directly.
                </Typography>
              </Paper>
            ) : fixComplianceIncomplete.length === 0 ? (
              <Paper elevation={0} sx={{
                p: 1.75, borderRadius: 2,
                border: '1px solid #BBF7D0', bgcolor: '#ECFDF5',
              }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#065F46', fontWeight: 700 }}>
                  Compliance is met. Close this and click Discharge.
                </Typography>
              </Paper>
            ) : (
              <>
                <Paper elevation={0} sx={{
                  p: 1.5, mb: 1.5, borderRadius: 2,
                  border: '1px solid #fde68a', bgcolor: '#FFFBEB',
                  display: 'flex', alignItems: 'flex-start', gap: 1.25,
                }}>
                  <Warning sx={{ color: '#b45309', fontSize: 22, mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#92400e', lineHeight: 1.2 }}>
                      Discharge is blocked until each admission day has ≥ {minVitalsForDay} vitals and ≥ {minNotesForDay} nursing note{minNotesForDay === 1 ? '' : 's'}.
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#7c2d12', mt: 0.5 }}>
                      <strong>Click any day below</strong> to fix just that date. You'll only be asked for the entries that are still missing on that specific day, and they'll be recorded against that date — not today.
                    </Typography>
                  </Box>
                </Paper>

                <Stack spacing={0.75} sx={{ maxHeight: 280, overflow: 'auto', mb: 0.5 }}>
                  {fixComplianceIncomplete.map((d) => {
                    const date = d.date || d.day;
                    const needs = [];
                    if ((d.missingVitals ?? 0) > 0) needs.push(`${d.missingVitals} more vital${d.missingVitals === 1 ? '' : 's'}`);
                    if ((d.missingNotes  ?? 0) > 0) needs.push(`${d.missingNotes} more note${d.missingNotes === 1 ? '' : 's'}`);
                    return (
                      <Paper
                        key={date}
                        elevation={0}
                        onClick={() => openFixDay(d)}
                        sx={{
                          p: 1.25, borderRadius: 1.5, cursor: 'pointer',
                          border: '1px solid #fde68a', bgcolor: '#fff',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: '#d97706', bgcolor: '#FFFBEB', boxShadow: '0 2px 8px rgba(217,119,6,0.12)' },
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.9rem' }}>
                            {date}
                          </Typography>
                          {needs.length > 0 && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#b45309', mt: 0.25 }}>
                              Need: {needs.join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>
                              vitals <strong>{d.vitals ?? 0}/{minVitalsForDay}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>
                              notes <strong>{d.notes ?? 0}/{minNotesForDay}</strong>
                            </Typography>
                          </Box>
                          <Chip
                            size="small" label="Fix"
                            sx={{
                              height: 22, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: '#d97706', color: '#fff',
                              '& .MuiChip-label': { px: 1 },
                            }}
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </>
            )
          )}

          {/* ── DAY DRILL-DOWN VIEW ──────────────────────────────────── */}
          {fixView === 'day' && fixSelectedDay && (() => {
            const needsVitals = (fixSelectedDay.missingVitals ?? 0) > 0;
            const needsNotes  = (fixSelectedDay.missingNotes  ?? 0) > 0;
            return (
              <>
                {/* Day header — what's still missing on this specific date */}
                <Paper elevation={0} sx={{
                  p: 1.5, mb: 1.5, borderRadius: 2,
                  border: '1px solid #fde68a', bgcolor: '#FFFBEB',
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Fixing date
                      </Typography>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#92400e', lineHeight: 1.2 }}>
                        {fixSelectedDay.date}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>
                        vitals <strong>{fixSelectedDay.vitals ?? 0}/{minVitalsForDay}</strong> · notes <strong>{fixSelectedDay.notes ?? 0}/{minNotesForDay}</strong>
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: '0.78rem', color: '#7c2d12' }}>
                    {needsVitals && needsNotes
                      ? `Still needs ${fixSelectedDay.missingVitals} more vital${fixSelectedDay.missingVitals === 1 ? '' : 's'} and ${fixSelectedDay.missingNotes} more nursing note${fixSelectedDay.missingNotes === 1 ? '' : 's'}.`
                      : needsVitals
                        ? `Still needs ${fixSelectedDay.missingVitals} more vital${fixSelectedDay.missingVitals === 1 ? '' : 's'}.`
                        : needsNotes
                          ? `Still needs ${fixSelectedDay.missingNotes} more nursing note${fixSelectedDay.missingNotes === 1 ? '' : 's'}.`
                          : 'This day is complete.'}
                  </Typography>
                </Paper>

                {/* Vitals entry — only shown if this day still needs vitals */}
                {needsVitals && (
                  <Paper elevation={0} sx={{
                    p: 1.75, mb: 1.5, borderRadius: 2,
                    border: '1px solid #e5e9ef', bgcolor: '#fff',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <MonitorHeart sx={{ color: '#d97706', fontSize: 20 }} />
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
                        Record vitals for {fixSelectedDay.date}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mb: 1.25 }}>
                      Temperature is required. Anything else can be left blank. This entry will be stamped with the date above, not today.
                    </Typography>
                    <Grid container spacing={1.25}>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          fullWidth size="small" required label="Temp (°C)"
                          type="number" inputProps={{ step: '0.1', min: 30, max: 45 }}
                          value={fixVitals.tem}
                          error={!!fixVitalErrors.tem}
                          helperText={fixVitalErrors.tem ? '30–45 °C required' : ''}
                          onChange={(e) => setFixVitals(v => ({ ...v, tem: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          fullWidth size="small" label="Pulse (bpm)" type="number"
                          value={fixVitals.pulse}
                          error={!!fixVitalErrors.pulse}
                          helperText={fixVitalErrors.pulse ? '40–200 bpm' : ''}
                          onChange={(e) => setFixVitals(v => ({ ...v, pulse: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          fullWidth size="small" label="Resp (br/min)" type="number"
                          value={fixVitals.res}
                          error={!!fixVitalErrors.res}
                          helperText={fixVitalErrors.res ? '8–40 br/min' : ''}
                          onChange={(e) => setFixVitals(v => ({ ...v, res: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          fullWidth size="small" label="BP (e.g. 120/80)"
                          value={fixVitals.pre}
                          error={!!fixVitalErrors.pre}
                          helperText={fixVitalErrors.pre ? 'Format: sys/dia' : ''}
                          onChange={(e) => setFixVitals(v => ({ ...v, pre: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          fullWidth size="small" label="SpO₂ (%)" type="number"
                          value={fixVitals.spo2}
                          error={!!fixVitalErrors.spo2}
                          helperText={fixVitalErrors.spo2 ? '70–100 %' : ''}
                          onChange={(e) => setFixVitals(v => ({ ...v, spo2: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth size="small" label="Weight (kg)" type="number"
                          value={fixVitals.we}
                          onChange={(e) => setFixVitals(v => ({ ...v, we: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          fullWidth size="small" label="Height (cm)" type="number"
                          value={fixVitals.high}
                          onChange={(e) => setFixVitals(v => ({ ...v, high: e.target.value }))}
                          disabled={fixSaving}
                        />
                      </Grid>
                    </Grid>
                    {(fixVitals.we && fixVitals.high) && (
                      <Typography sx={{ mt: 1, fontSize: '0.75rem', color: '#475569' }}>
                        Computed BMI: <strong>{fixCalcBMI(fixVitals.we, fixVitals.high) || '—'}</strong>
                      </Typography>
                    )}
                    <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveFixVitals}
                        disabled={fixSaving || !fixCompliancePatient}
                        startIcon={<SaveIcon />}
                        sx={{
                          textTransform: 'none', fontWeight: 700, px: 2.5,
                          bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' },
                        }}
                      >
                        {fixSaving ? 'Saving…' : `Save Vitals for ${fixSelectedDay.date}`}
                      </Button>
                    </Box>
                  </Paper>
                )}

                {/* Nursing note entry — only shown if this day still needs a note */}
                {needsNotes && (
                  <Paper elevation={0} sx={{
                    p: 1.75, borderRadius: 2,
                    border: '1px solid #e5e9ef', bgcolor: '#fff',
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <NoteAdd sx={{ color: '#4338ca', fontSize: 20 }} />
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>
                        Record nursing note for {fixSelectedDay.date}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mb: 1.25 }}>
                      One saved note per day satisfies the per-day minimum. This entry will be stamped with the date above, not today.
                    </Typography>
                    <TextField
                      fullWidth multiline minRows={5} placeholder="Nursing observations, interventions, patient response…"
                      value={fixNote}
                      onChange={(e) => setFixNote(e.target.value)}
                      disabled={fixSaving}
                    />
                    <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveFixNote}
                        disabled={fixSaving || !fixCompliancePatient || !fixNote.trim()}
                        startIcon={<SaveIcon />}
                        sx={{
                          textTransform: 'none', fontWeight: 700, px: 2.5,
                          bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' },
                        }}
                      >
                        {fixSaving ? 'Saving…' : `Save Note for ${fixSelectedDay.date}`}
                      </Button>
                    </Box>
                  </Paper>
                )}

                {/* Edge case: day was already complete by the time the
                    nurse drilled in (e.g., they navigated back-and-forth). */}
                {!needsVitals && !needsNotes && (
                  <Paper elevation={0} sx={{
                    p: 1.75, borderRadius: 2,
                    border: '1px solid #BBF7D0', bgcolor: '#ECFDF5',
                  }}>
                    <Typography sx={{ fontSize: '0.88rem', color: '#065F46', fontWeight: 700 }}>
                      This day is already complete. Go back to pick another day or close to discharge.
                    </Typography>
                  </Paper>
                )}
              </>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 2.25, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e5e9ef' }}>
          {fixView === 'day' && (
            <Button
              onClick={backToStatus}
              disabled={fixSaving}
              sx={{ color: '#4338ca', textTransform: 'none', fontWeight: 700, mr: 'auto' }}
            >
              ← Back to days
            </Button>
          )}
          <Button onClick={closeFixCompliance} disabled={fixSaving} sx={{ color: '#64748b', textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      </Container>
    </Box>
  );
}